import { Component, Input, OnChanges, SimpleChanges, TemplateRef } from '@angular/core';
import { translate } from '@jsverse/transloco';

import { AppSharedModule } from '../../../../../app-shared.module';
import { SmartSummary } from '../../../../../providers/storage-api/interactive-smart-summary.service';

interface SummarySegment {
  text: string;
  highlighted: boolean;
}

interface HighLightSegmentLocation {
  char_length: number;
  char_offset: number;
  length: number;
  offset: number;
  section: number;
  section_char_offset: number;
}
@Component({
  selector: 'app-smart-summary-text',
  imports: [AppSharedModule],
  templateUrl: './smart-summary-text.component.html',
  styleUrls: ['./smart-summary-text.component.scss'],
})
export class SmartSummaryTextComponent implements OnChanges {
  @Input() smartSummary: SmartSummary;
  @Input() menuTemplate: TemplateRef<any>;
  @Input() tooltipText = '';
  summarySegments: SummarySegment[] = [];
  tooltipDisplayText = '';

  ngOnChanges(changes: SimpleChanges): void {
    this.createSegments();
    if (changes.tooltipText.currentValue) {
      this.tooltipDisplayText = translate(this.tooltipText);
    }
  }

  /**
   * Breaks the smart summary into segments.
   * If there are highlighted parts (entities or taxonomies), then the segments are broken up
   * into highlighted and non-highlighted parts. Otherwise, the entire summary is returned as one segment.
   * @private
   */
  private createSegments() {
    const locations = this.getHighlightLocationsForSegment([
      ...this.topEntities,
      ...this.topTaxonomies,
    ]);
    if (!locations.length) {
      this.summarySegments = [{ text: this.smartSummary.summary, highlighted: false }];
      return;
    }
    this.summarySegments = this.splitTextIntoSegments(this.smartSummary.summary, locations);
  }

  get taxonomies() {
    if (!this.smartSummary?.summaryAnalysis?.taxonomy) {
      return [];
    }
    return this.smartSummary.summaryAnalysis.taxonomy.filter(
      (t) => t.id !== 'UNCATEGORIZED' && t.leaf
    );
  }

  get topTaxonomies() {
    return this.getTopThreeItemsBySentimentScore(this.taxonomies);
  }

  /**
   * @description
   * Given an array of items with a sentimentScore, reduce it to the top three by sentimentScore.
   * If the array has less than three items, add them all to the top array.
   * Otherwise, replace the item with the lowest sentimentScore in the top array
   * with the current item if the current item has a higher sentimentScore.
   * @param array The array of items to reduce.
   * @returns The top three items from the array by sentimentScore.
   */
  private getTopThreeItemsBySentimentScore(array) {
    return [...array].sort((a, b) => b.sentimentScore - a.sentimentScore).slice(0, 3);
  }

  get entities() {
    if (!this.smartSummary?.summaryAnalysis?.entities) {
      return [];
    }
    return this.smartSummary.summaryAnalysis.entities.filter(
      (e) => e.type === 'Person' || e.type === 'user'
    );
  }

  get topEntities() {
    return this.getTopThreeItemsBySentimentScore(this.entities);
  }

  /**
   * Extracts and deduplicates highlight locations from a collection of summary analyses.
   *
   * @param summaryAnalyses - An array of analysis objects each containing taxonomy elements
   *                          and entity mentions with associated locations.
   * @returns An array of unique `HighLightSegmentLocation` objects sorted by offset.
   */

  private getHighlightLocationsForSegment(summaryAnalyses): Array<HighLightSegmentLocation> {
    const flattenedLocations = summaryAnalyses.flatMap((analysis) => {
      let taxonomyElements = [];
      let entities = [];
      if (analysis?.taxonomyElements?.length) {
        taxonomyElements = analysis.taxonomyElements.flatMap((te) =>
          te.mentions.flatMap((mention) => mention.locations)
        );
      }
      if (analysis?.mentions?.length) {
        entities = analysis.mentions.flatMap((mention) => mention.locations);
      }
      return taxonomyElements.concat(...entities);
    });

    // Deduplicate locations using a Map with a composite key.
    const uniqueMap = new Map<string, HighLightSegmentLocation>();
    flattenedLocations.forEach((loc) => {
      // Create a composite key from selected properties.
      const key = `${loc.offset}-${loc.length}-${loc.section}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, loc);
      }
    });

    // Return the unique location objects as an array.
    return Array.from(uniqueMap.values()).sort((a, b) => a.offset - b.offset);
  }

  /**
   * Splits the given text into segments.
   * The segments that fall within any location are marked as highlighted.
   *
   * @param text - The original string.
   * @param locations - An array of Location objects (unsorted or sorted).
   * @returns An array of Segment objects covering the entire text.
   */
  private splitTextIntoSegments(
    text: string,
    locations: HighLightSegmentLocation[]
  ): SummarySegment[] {
    // Ensure the locations are processed in ascending order.
    const sortedLocations = locations.slice().sort((a, b) => a.offset - b.offset);
    const segments: SummarySegment[] = [];
    let currentIndex = 0;

    sortedLocations.forEach((loc) => {
      // If there's non-highlighted text before the location, add that as a segment.
      if (loc.offset > currentIndex) {
        segments.push({
          text: text.substring(currentIndex, loc.offset),
          highlighted: false,
        });
      }
      // Add the highlighted segment.
      segments.push({
        text: text.substring(loc.offset, loc.offset + loc.length),
        highlighted: true,
      });
      // Move the current index past the highlighted part.
      currentIndex = loc.offset + loc.length;
    });

    // If there is any text left after the last location, add it as a non-highlighted segment.
    if (currentIndex < text.length) {
      segments.push({
        text: text.substring(currentIndex),
        highlighted: false,
      });
    }

    return segments;
  }
}
